import { claw, crack, dots, flames, groundRing, shards, shield, sprite, star, target, waves } from '../kit';
import { type Pix } from '../pix';
import { registerIcons } from '../store';

/**
 * กรงเล็บคีมไฟ (cinnabar, yang): the forge-tiger's claws. Re-themed from blood to heat (10-13): hot iron claws, ember
 * rakes, sparks and soot; no blood drops anywhere.
 */

const EMBER = '#ff7a2a';
const EMBER_HI = '#ffd27a';
const CINNABAR = '#e0452a';
const IRON = '#a06a52';
const HOT = '#ffe6c0';
const SOOT = '#3a2620';

/** Iron claws glowing at the nails. */
const hotClaw = (p: Pix, x: number, y: number, flip = false) => claw(p, x, y, IRON, EMBER, { flip });

/** Three ember rakes from (x0,y0) slanting down by (dx,dy), the hot core lit. */
function rakes(p: Pix, x0: number, y0: number, dx: number, dy: number, gap = 5, w = 3): void {
  for (let k = 0; k < 3; k++) {
    const x = x0 + k * gap;
    p.thick(x, y0, x + dx, y0 + dy, CINNABAR, w);
    p.line(x, y0, x + dx, y0 + dy, EMBER_HI);
  }
}

/** Sparks: little 1-2 px cinders. */
function cinders(p: Pix, list: readonly (readonly [number, number])[]): void {
  for (const [x, y] of list) p.px(x, y, EMBER_HI).px(x + 1, y, EMBER);
}

/** A tiger's head, front on, 12 wide (ears, stripes, hot eyes). */
function tigerHead(p: Pix, x: number, y: number): void {
  sprite(
    p,
    x,
    y,
    [
      'o..........o',
      'oo........oo',
      'oooooooooooo',
      'oossoooossoo',
      'oeeoooooeeoo',
      'oooooooooooo',
      '.oossnnssoo.',
      '.ooowmmwooo.',
      '..owmmmmwo..',
      '...oooooo...',
    ],
    { o: EMBER, s: SOOT, e: EMBER_HI, n: SOOT, w: HOT, m: CINNABAR },
  );
}

registerIcons('skill', {
  // A hot-iron claw rakes three glowing furrows.
  'bc-rake': (p) => {
    rakes(p, 10, 12, -6, 16);
    hotClaw(p, 12, 2);
    cinders(p, [
      [26, 18],
      [24, 24],
      [4, 26],
    ]);
  },
  // A tiger's bite crushing a guard: hot fangs closing on a cracked shield.
  'bc-crush': (p) => {
    shield(p, 16, 9, 14, 15, '#a89888', '#d8ccc0');
    crack(p, [
      [16, 10],
      [14, 15],
      [18, 18],
      [15, 23],
    ], SOOT);
    for (let k = 0; k < 4; k++) {
      p.poly([[6 + k * 6, 6], [10 + k * 6, 6], [8 + k * 6, 12]], HOT);
      p.poly([[6 + k * 6, 27], [10 + k * 6, 27], [8 + k * 6, 21]], HOT);
    }
    p.rect(4, 3, 24, 3, EMBER).rect(4, 27, 24, 3, EMBER);
    shards(p, 16, 17, 13, EMBER_HI, 6, 0.4);
  },
  // Two claws crossing, a double rake.
  'bc-maul': (p) => {
    rakes(p, 4, 6, 8, 20, 4, 2);
    rakes(p, 18, 6, -8, 20, 4, 2);
    hotClaw(p, 1, 1);
    hotClaw(p, 17, 1, true);
  },
  // The tiger leaps across the gap onto a marked spot, an ember arc behind.
  'bc-pounce': (p) => {
    groundRing(p, 21, 26, 8, 3, CINNABAR);
    for (let k = 0; k <= 8; k++) {
      const a = Math.PI * (1 - k / 8);
      p.rect(Math.round(13 + Math.cos(a) * 10), Math.round(16 - Math.sin(a) * 12), 2, 2, k % 2 ? EMBER : EMBER_HI);
    }
    dots(p, 2, 27, 12, 27, EMBER, 3);
    hotClaw(p, 14, 12);
  },
  // The roar: heat waves pouring from a tiger's open jaw.
  'bc-roar': (p) => {
    tigerHead(p, 2, 9);
    waves(p, 13, 17, 5, 3, EMBER, 0.85, 4);
    cinders(p, [
      [27, 8],
      [28, 25],
    ]);
  },
  // The feast: claws sink into the wound and the heat flows back up the arm.
  'bc-feast': (p) => {
    target(p, 16, 20, 7, CINNABAR);
    hotClaw(p, 9, 4);
    for (const [x, y] of [
      [24, 14],
      [26, 10],
      [27, 6],
    ] as const)
      p.rect(x, y, 2, 2, EMBER_HI);
    p.px(27, 4, HOT).px(28, 3, HOT);
  },
  // The tiger's blood boils (a held guard): a tiger head over a kiln blaze.
  'bc-fury': (p) => {
    flames(p, 3, 30, 26, 14, CINNABAR, EMBER_HI);
    tigerHead(p, 10, 5);
    p.rect(6, 22, 20, 1, HOT);
  },
  // The heaven-mauling tiger: three huge rakes and a star of heat.
  'bc-tiger': (p) => {
    flames(p, 2, 30, 28, 8, CINNABAR, EMBER);
    rakes(p, 8, 3, 6, 22, 6, 4);
    star(p, 24, 8, 4, HOT);
    star(p, 6, 26, 2, EMBER_HI);
    hotClaw(p, 1, 0);
  },
});
